import React, { useState } from 'react';
import { Controller, Control } from 'react-hook-form';
import Select, {
  SingleValue,
  InputActionMeta,
  StylesConfig,
} from 'react-select';
import type { BankAccount } from '@/types';
import type { BranchOption } from './BankAccountForm';

const branchSelectStyles: StylesConfig<BranchOption, false> = {
  option: (provided) => ({
    ...provided,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    ':hover': {
      ...provided[':hover'],
      backgroundColor: 'transparent',
      cursor: 'pointer',
    },
    ':active': {
      ...provided[':active'],
      backgroundColor: 'transparent',
      cursor: 'pointer',
    },
  }),
};

type Props = {
  control: Control<BankAccount>;
  branchOptions: BranchOption[];
  initialBranchName?: string;
  onBranchChange: (
    option: BranchOption | null,
    fieldOnChange: (value: string) => void
  ) => void;
  loadBranches: () => void;
};

const BranchSelect: React.FC<Props> = ({
  control,
  branchOptions,
  initialBranchName,
  onBranchChange,
  loadBranches,
}) => {
  const [searchInput, setSearchInput] = useState('');

  const handleBranchInputChange = (
    value: string,
    actionMeta: InputActionMeta
  ) => {
    if (actionMeta.action === 'input-change') {
      setSearchInput(value);
    }
    if (actionMeta.action === 'menu-close') {
      setSearchInput('');
    }
    return value;
  };

  return (
    <Controller
      name='branchName'
      control={control}
      rules={{ required: '支店名は必須です' }}
      defaultValue={initialBranchName ?? ''}
      render={({ field }) => {
        const selectedOption =
          branchOptions.find(
            (opt) => opt.branch.name === (field.value ?? '')
          ) ?? null;

        return (
          <Select<BranchOption, false>
            inputId='searchBranch'
            placeholder='支店名を検索する'
            classNamePrefix='branch-select'
            options={branchOptions}
            value={selectedOption}
            inputValue={searchInput}
            onMenuOpen={loadBranches}
            onInputChange={handleBranchInputChange}
            onChange={(option: SingleValue<BranchOption>) =>
              onBranchChange(option ?? null, field.onChange)
            }
            onBlur={field.onBlur}
            isClearable
            openMenuOnClick={false}
            openMenuOnFocus={false}
            menuIsOpen={searchInput.trim().length > 0}
            styles={branchSelectStyles}
            filterOption={(option, rawInput) => {
              const keyword = rawInput.trim();
              if (!keyword) return false;

              const { branch } = option.data;

              const startsWith = (text?: string | null) =>
                text != null && text.startsWith(keyword);

              return (
                startsWith(branch.name) ||
                startsWith(branch.hira) ||
                startsWith(branch.kana)
              );
            }}
            noOptionsMessage={() =>
              searchInput.trim().length === 0
                ? '文字を入力してください'
                : '該当の支店は見つかりません'
            }
          />
        );
      }}
    />
  );
};

export default BranchSelect;
